import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import FilteredComponentHeader from "../Components/FilteredComponentHeader";
import FilteredLeft from "../Components/FilteredLeft";
import ProductCarousel from "../Components/ProductCarousel";
import { convertToPersian } from "../Functions/TextTransform";
import { useProductPageStickyLayout } from "../Hooks/useProductPageStickyLayout";
import { useProductsScrollToTop } from "../Hooks/useProductsScrollToTop";
import * as client from "../Services/client";
import { BsChevronUp } from "react-icons/bs";
import _ from "lodash";

/**
 * @param {string} value
 */
const decodeAndSplitValues = (value) => {
  return _.compact(decodeURIComponent(value.replace(/\+/g, " ")).replace(/_/g, " ").split("-"));
};

function Collection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { catID } = useParams();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [selectedSort, setSelectedSort] = useState(undefined);
  const [catObject, setCatObject] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [pageLanguage, setPageLanguage] = useState(
    location.pathname.split("").slice(1, 3).join("")
  );
  const [prevUrlSearch, setPrevUrlSearch] = useState("");
  const [checkedItems, setCheckedItems] = useState({});
  const [checkedPriceLevels, setCheckedPriceLevels] = useState({});
  const [checkedMaterial, setCheckedMaterial] = useState({});
  const [titleProduct, setTitleProduct] = useState([]);
  const [colorFilter, setColorFilter] = useState([]);
  const [priceLevels, setPriceLevels] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [changeTrigger, setChangeTrigger] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [changeData, setChangeData] = useState({
    item: null,
    type: null,
    category: null,
  });
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 923px)" });
  const stickyZoneRef = useRef(null);
  const filterBarRef = useRef(null);
  const productsListRef = useRef(null);

  useProductPageStickyLayout(isTabletOrMobile, stickyZoneRef, filterBarRef, {
    pinClass: "collection_page_sticky_zone_is_stuck",
    headerOffsetVar: "--collection-page-header-offset",
    sidebarTopVar: "--collection-page-sidebar-sticky-top",
  });

  const getCats = useCallback(() => {
    client
      .getCats(catID)
      .then((response) => {
        if (response.data["Link"] !== undefined && response.data["Link"] !== "") {
          setCatObject(response.data);
        } else {
          setCatObject({});
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [catID]);

  const fetchFilteredProducts = async ({ searchParams } = {}) => {
    if (!searchParams) {
      return;
    }

    const hasSearchParams = searchParams.toString() !== "";
    const pathName = location.pathname;
    const groupedParams = {};
    searchParams.forEach((value, key) => {
      if (groupedParams[key]) {
        groupedParams[key] += `-${value}`;
      } else {
        groupedParams[key] = value;
      }
    });
    const formattedParams = Object.entries(groupedParams)
      .map(([key, value]) => {
        if (value.includes(" ")) {
          return `${key}=${value.replace(/ /g, "_")}`;
        }
        return `${key}=${value}`;
      })
      .join("&");

    let newURL = `${pathName}?${formattedParams}`;
    if (!pathName.includes("filters") && hasSearchParams) {
      newURL = `${pathName}/filters?${formattedParams}`;
    }

    if (pathName.includes("filters") && !hasSearchParams) {
      const updatedURL = pathName.replace("/filters", "");
      newURL = `${updatedURL}`;
    }
    navigate(newURL, { replace: true });

    let currentUrlParams = new URLSearchParams();
    searchParams?.forEach((value, key) => {
      if (["color", "type", "material", "price"].includes(key)) {
        const splitValues = decodeAndSplitValues(value) || [];
        splitValues.forEach((item) => {
          currentUrlParams.append(key, item);
        });
      } else {
        currentUrlParams.append(key, value);
      }
    });

    const linkString = catObject.Link || "";
    const linkArray = linkString.split(",");
    const groupIdsArray = linkArray.reverse().map((link) => `group=${link}`);
    const groupQuery = groupIdsArray.join("&");
    try {
      const url =
        currentUrlParams.size > 0
          ? `${client.baseUrlCatProduct}?${groupQuery}&${currentUrlParams.toString()}`
          : `${client.baseUrlCatProduct}?${groupQuery}`;

      const response = await axios.get(url);

      setProductGroups(response.data["Filters"]["ProductGroups"]);
      setTitleProduct(response.data["Filters"]["TypeFilters"]);
      setColorFilter(response.data["Filters"]["Colors"]);
      setPriceLevels(response.data["Filters"]["PriceLevels"]);

      const allProducts = response.data.Products;

      setFilteredProducts(allProducts.reverse());

      const selectedColorIds = currentUrlParams.getAll("colors");
      const selectedRodMaterials = currentUrlParams.getAll("material");
      const selectedPrice = currentUrlParams.getAll("priceLevels");

      if (selectedColorIds.length > 0) {
        const selectedColors = response.data["Filters"]["Colors"].filter((color) =>
          selectedColorIds.includes(color.ColorId.toString())
        );

        selectedColors.forEach((color) => {
          if (!selectedItems.some((item) => item.ColorId === color.ColorId)) {
            setSelectedItems((prevItems) => [...prevItems, color]);
          }
        });
      }

      if (selectedRodMaterials.length > 0) {
        const selectedMaterials = response.data["Filters"]["TypeFilters"]
          .find((filter) => filter.FilterName === "material")
          ?.Values.filter((material) => selectedRodMaterials.includes(material.Value.toString()));
        selectedMaterials.forEach((material) => {
          if (!selectedItems.some((item) => item.Value === material.Value)) {
            setSelectedItems((prevItems) => [...prevItems, material]);
          }
        });
      }

      if (selectedPrice.length > 0) {
        const selectedPrices = response.data["Filters"]["PriceLevels"].filter((price) =>
          selectedPrice.includes(price.ProductPriceLevelId.toString())
        );

        selectedPrices.forEach((price) => {
          if (
            !selectedItems.some((item) => item.ProductPriceLevelId === price.ProductPriceLevelId)
          ) {
            setSelectedItems((prevItems) => [...prevItems, price]);
          }
        });
      }
    } catch (error) {
      console.error("خطا در دریافت اطلاعات:", error);
    }
  };

  const handleChange = (item, category, type) => {
    if (item && category) {
      if (type === 1) {
        setSelectedItems((prevSelectedItems) => {
          if (item.ColorId) {
            if (!prevSelectedItems.some((i) => i.ColorId === item.ColorId)) {
              return [...prevSelectedItems, item];
            }
          } else if (item.ProductPriceLevelId) {
            if (
              !prevSelectedItems.some((i) => i.ProductPriceLevelId === item.ProductPriceLevelId)
            ) {
              return [...prevSelectedItems, item];
            }
          } else if (item.Value) {
            if (!prevSelectedItems.some((i) => i.Value === item.Value)) {
              return [...prevSelectedItems, item];
            }
          }
          return prevSelectedItems;
        });
      } else if (type === 0) {
        setSelectedItems((prevSelectedItems) => {
          if (item.ColorId) {
            return prevSelectedItems.filter((i) => i.ColorId !== item.ColorId);
          } else if (item.ProductPriceLevelId) {
            return prevSelectedItems.filter(
              (i) => i.ProductPriceLevelId !== item.ProductPriceLevelId
            );
          } else if (item.Value) {
            return prevSelectedItems.filter((i) => i.Value !== item.Value);
          } else if (item.ProductGroupId) {
            return prevSelectedItems.filter((i) => i.ProductGroupId !== item.ProductGroupId);
          }
          return prevSelectedItems;
        });
      }
    }

    setChangeData({ item, type, category });
    setChangeTrigger((prev) => !prev);

    if (selectedItems.length === 1) {
      const currentUrl = new URL(window.location.href);
      currentUrl.search = "";
      window.history.replaceState(null, "", currentUrl);
    }
  };

  const handleRemoveItem = (urlName) => {
    const currentUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams(currentUrl.search);

    for (const [key, value] of [...searchParams.entries()]) {
      const decodedValues = decodeAndSplitValues(value);

      if (decodedValues.includes(urlName)) {
        const allValues = searchParams.getAll(key);
        searchParams.delete(key);
        const updatedValues = allValues
          .flatMap((val) => decodeAndSplitValues(val))
          .filter((val) => val !== urlName);
        updatedValues.forEach((val) => searchParams.append(key, val));
      }
    }

    const updatedSelectedItems = [...searchParams.entries()].flatMap(([key, value]) => {
      return decodeAndSplitValues(value).map((item) => ({
        Key: key,
        UrlName: item,
      }));
    });
    setSelectedItems(updatedSelectedItems);
    fetchFilteredProducts({ searchParams });
  };

  useEffect(() => {
    getCats();
  }, [getCats]);

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }

    const updateURLAndFetch = (paramName) => {
      const { UrlName } = changeData.item;
      let searchParams;
      if (selectedItems.length === 1) {
        searchParams = new URLSearchParams(window.location.search);
      } else {
        searchParams = new URLSearchParams(location.search);
      }

      const prefixedUrlName = UrlName.replace(/ /g, "_");
      const currentGroups = searchParams.getAll(paramName);

      if (changeData.type === 1 || changeData.type === 3) {
        if (!currentGroups.includes(prefixedUrlName)) {
          searchParams.append(paramName, prefixedUrlName);
        }
      } else if (changeData.type === 0 || changeData.type === 2) {
        const updatedGroups = currentGroups.map((group) => {
          const encodedGroup = decodeURIComponent(group);
          return encodedGroup
            .split("-")
            .filter((g) => g !== prefixedUrlName)
            .join("-");
        });

        const filteredGroups = updatedGroups.filter((group) => group !== "");
        searchParams.delete(paramName);
        filteredGroups.forEach((group) => searchParams.append(paramName, group));
      }
      const hasSearchParams = searchParams.toString() === "";
      let updatedURL;
      const pathName = location.pathname;
      if (pathName.includes("filters") && hasSearchParams) {
        updatedURL = location.pathname.replace("/filters", "");
        const newURL = `${updatedURL}?${searchParams.toString()}`;
        navigate(newURL, { replace: true });
      }
      const updatedSelectedItems = [...searchParams.entries()].flatMap(([key, value]) => {
        return decodeAndSplitValues(value).map((item) => ({
          Key: key,
          UrlName: item,
        }));
      });
      setSelectedItems(updatedSelectedItems);

      if (updatedSelectedItems.length >= 0) {
        fetchFilteredProducts({ searchParams });
      }
    };

    switch (changeData?.category) {
      case "product":
        updateURLAndFetch("type");
        break;
      case "Material":
        updateURLAndFetch("material");
        break;
      case "colors":
        updateURLAndFetch("color");
        break;
      case "priceLevels":
        updateURLAndFetch("price");
        break;
      default:
        break;
    }
  }, [changeTrigger, changeData]);

  useEffect(() => {
    if (catObject && Object.keys(catObject).length > 0) {
      const searchParams = new URLSearchParams(location.search);
      fetchFilteredProducts({ searchParams });
    }
  }, [catObject]);

  useEffect(() => {
    const currentLang = location.pathname.split("").slice(1, 3).join("");
    if (pageLanguage !== currentLang) {
      setPageLanguage(currentLang);
    }

    const startIndex = 3;
    const endIndex = location.pathname.indexOf("/", startIndex + 1);
    const urlSearch = location.pathname.substring(startIndex, endIndex);

    if (urlSearch !== prevUrlSearch) {
      setPrevUrlSearch(urlSearch);
      getCats();
    }
  }, [location.pathname]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const result = [...searchParams.entries()]
      .flatMap(([key, value]) => {
        return decodeAndSplitValues(value).map((item) => ({
          Key: key,
          UrlName: item,
        }));
      })
      .filter(Boolean);
    setSelectedItems(result);
  }, []);

  const { showScrollToTop, scrollToTop } = useProductsScrollToTop(
    productsListRef,
    null,
    filteredProducts.length,
    {
      isTabletOrMobile,
      productItemSelector: ":scope > li.products_collection_item",
    }
  );

  return (
    <div>
      <div className="Product_page_container font_farsi">
        <div
          className="collection_page_sticky_zone collection_page_filter_bar_sticky"
          ref={stickyZoneRef}
        >
          <div className="Product_page_header collection_page_title_in_sticky">
            <h1>
              {pageLanguage === "en" ? catObject["EnTitle"] : convertToPersian(catObject["Title"])}
            </h1>
          </div>
          <FilteredComponentHeader
            filteredProducts={filteredProducts}
            setShowAllFilters={setShowAllFilters}
            showAllFilters={showAllFilters}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            setSelectedItems={setSelectedItems}
            selectedItems={selectedItems}
            setCheckedItems={setCheckedItems}
            handleRemoveItem={handleRemoveItem}
            fetchFilteredProducts={fetchFilteredProducts}
            priceLevels={priceLevels}
            syncProductLayout
            filterBarRef={filterBarRef}
          />
        </div>
        <div className="Product_page_content collection_page_body_sync">
          <div
            className={
              showAllFilters
                ? "filter-open collection_page_filters_col"
                : "filter-closed collection_page_filters_col"
            }
          >
            {showAllFilters && (
              <div className="collection_page_filter_inner">
                <div className="nav_header nav_header_single">
                  <div className="nav_header_bottom nav_header_bottom_single">
                    <div
                      className="close_menu_icon"
                      onClick={() => {
                        setShowAllFilters(false);
                      }}
                    >
                      <img
                        src={require("../Images/public/close-button.png")}
                        className="img-fluid"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <FilteredLeft
                  titleProduct={titleProduct}
                  selectedItems={selectedItems}
                  handleChange={handleChange}
                  setCheckedItems={setCheckedItems}
                  checkedItems={checkedItems}
                  checkedPriceLevels={checkedPriceLevels}
                  setCheckedPriceLevels={setCheckedPriceLevels}
                  colorFilter={colorFilter}
                  priceLevels={priceLevels}
                  setCheckedMaterial={setCheckedMaterial}
                  checkedMaterial={checkedMaterial}
                  productGroups={productGroups}
                  setSelectedItems={setSelectedItems}
                  syncProductLayout
                />
              </div>
            )}
          </div>

          <div
            className={
              showAllFilters
                ? "carousel-open collection_page_products_col"
                : "carousel-closed collection_page_products_col"
            }
          >
            {filteredProducts.length > 0 && (
              <ProductCarousel
                filteredProducts={filteredProducts}
                selectedSort={selectedSort || "Price, High to Low"}
                colorParams={decodeAndSplitValues(
                  new URLSearchParams(location.search).getAll("color").join("-")
                )}
                listRef={productsListRef}
              />
            )}
          </div>
        </div>
      </div>
      {showScrollToTop && (
        <button
          type="button"
          className="products_page_scroll_to_top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <BsChevronUp />
        </button>
      )}
    </div>
  );
}

export default Collection;







